import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, ReplaySubject } from 'rxjs';
import { catchError, map, shareReplay, tap } from 'rxjs/operators';
import { ApplicationConfigService } from './application-config.service';
import { User } from '../models/user.model';
import { ICustomer } from '../models/customer.model';
import { AuthServerProvider } from './auth-jwt.service';
import { LoginExistResultType } from '../models/enums/login-exist-result-type.enum';
import { AuthorityEnum } from '../models/enums/authority.enum';
import { environment } from '../environments/environment';

@Injectable({ providedIn: 'root' })
export class AccountService {
  readonly #authServerProvider = inject(AuthServerProvider);
  readonly #http = inject(HttpClient);
  readonly #applicationConfigService = inject(ApplicationConfigService);

  private userIdentity: User | null = null;
  private authenticationState = new ReplaySubject<User | null>(1);
  private accountCache$?: Observable<User> | null;

  public save(account: User): Observable<Object> {
    //Aucune valeur de retour
    return this.#http.post(this.#applicationConfigService.getEndpointFor('api/account'), account);
  }

  public authenticate(identity: User | null): void {
    this.userIdentity = identity;
    this.authenticationState.next(this.userIdentity);
    if (!identity) {
      this.accountCache$ = null;
    }
  }

  public getAuthority(): string | undefined {
    return (
      this.userIdentity?.authorities.find((authority: string) => authority === AuthorityEnum.ADMIN) ||
      this.userIdentity?.authorities.find((authority: string) => authority === AuthorityEnum.MODERATOR) ||
      this.userIdentity?.authorities.find((authority: string) => authority === AuthorityEnum.USER)
    );
  }

  public hasAnyAuthority(authorities: string[] | string): boolean {
    if (!this.userIdentity) {
      return false;
    }
    if (!Array.isArray(authorities)) {
      authorities = [authorities];
    }
    return this.userIdentity.authorities.some((authority: string) => authorities.includes(authority));
  }

  public identity(): Observable<User | null> {
    if (this.accountCache$) {
      return this.accountCache$.pipe(catchError(() => of(null)));
    }
    if (this.#authServerProvider.getToken()) {
      return (this.accountCache$ = this.fetch().pipe(
        tap((account: any) => this.authenticate(account)),
        shareReplay()
      ));
    }
    return of(null);
  }

  public isAuthenticated(): boolean {
    return this.userIdentity !== null;
  }

  public getAuthenticationState(): Observable<User> {
    return this.authenticationState.asObservable().pipe(map(val => new User(val)));
  }

  getCurrentUser(): Observable<User> {
    return this.#http.get<User>(this.#applicationConfigService.getEndpointFor('api/account'));
  }

  checkIsEmailExist(login: string): Observable<LoginExistResultType> {
    return this.#http.get<LoginExistResultType>(this.#applicationConfigService.getEndpointFor('api/login/exist') + '?login=' + login);
  }

  resendAccountActivationEmail(login: string): Observable<string> {
    return this.#http.get(this.#applicationConfigService.getEndpointFor('api/account/send-activation-email') + '?login=' + login, { responseType: 'text' });
  }

  checkCommitmentAccepted(customer?: ICustomer): boolean {
    return customer?.acceptedCGV ?? false;
  }

  private fetch(): Observable<User> {
    return this.#http.get<User>(this.#applicationConfigService.getEndpointFor('api/account'));
  }

  public updatePassword(newPassword: string, currentPassword: string): Observable<{}> {
    return this.#http.post(environment.urlApi + 'api/account/change-password', { currentPassword, newPassword });
  }
}
